import { Skeleton } from '@finn/design-system/atoms/skeleton';
import { cn, useCurrentLocale } from '@finn/ui-utils';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useInViewport } from 'react-in-viewport';

import { VideoCosmicMetadata } from './meta';

const getId = (uri: string) => {
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = uri.match(regExp);

  return match && match[2].length === 11 ? match[2] : null;
};

type Props = {
  data: VideoCosmicMetadata;
};

const Video: React.FunctionComponent<Props> = ({ data }: Props) => {
  const { region } = useCurrentLocale();

  const [loadVideo, setLoadVideo] = useState(!data.lazy_load);
  const containerRef = useRef(null);
  const { inViewport } = useInViewport(
    containerRef,
    {
      threshold: 0.2,
    },
    { disconnectOnLeave: true },
    {}
  );
  useEffect(() => {
    if (inViewport) {
      setLoadVideo(true);
    }
  }, [inViewport]);

  const parameterizedUri = useMemo(() => {
    const videoId = getId(data.youtube_uri);
    const newUri = `https://www.youtube.com/embed/${videoId}`;

    return `${newUri}?color=white&playinline=1&rel=0&hl=${region}&disablekb=1`;
  }, [data, region]);

  return (
    <div ref={containerRef}>
      <div
        className={cn(
          'mx-auto my-0 w-full max-w-[1260px]',
          data?.has_top_padding ? 'md: pt-24' : 'pt-40'
        )}
      >
        {!loadVideo && (
          <Skeleton className="h-[calc(min(100vw,1260px)*9/16)] w-full border-none" />
        )}
        {loadVideo && (
          <iframe
            className="h-[calc(min(100vw,1260px)*9/16)] w-full border-none"
            src={parameterizedUri}
            loading="lazy"
            title="YouTube Player"
            allow="fullscreen"
            sandbox="allow-scripts allow-same-origin allow-presentation"
            referrerPolicy="no-referrer-when-downgrade"
          />
        )}
      </div>
    </div>
  );
};

export default Video;
